<template>
  <div class="compare-page">
    <div class="l-container header-title-wrap has-buttons">
      <h1 class="text-h1-bold-up">Сравнение товаров</h1>
    </div>
    <div ref="compareContainer" class="l-container compare-wrap">
      <template v-if="ids.length">
        <UiHorizontalScroll>
          <div class="categories-list">
            <ui-ui-button
              v-for="item in categories"
              :key="item.id"
              size="medium"
              :type="item.id === selectedCategory.id ? 'primary' : 'secondary'"
              class="category-btn"
              @click="onCategoryChange(item)"
            >
              <span class="category-name"
                ><span>{{ item?.name }}</span></span
              >
              <span class="category-count">{{
                productsByCategory[item.id].length
              }}</span>
            </ui-ui-button>
          </div>
        </UiHorizontalScroll>
        <template v-if="productsByCategory">
          <CompareProducts
            :products="productsByCategory[selectedCategory.id]"
            :specifications="specifications[selectedCategory.id]"
            :category="selectedCategory"
            @delete="deleteItem"
          />

          <div class="clear-category lp-m-b">
            <ui-ui-link icon-before="trash" @click="confirmDelete()">
              Очистить список
            </ui-ui-link>
          </div>
        </template>
      </template>
      <UiEmptyState
        v-else
        class="lp-m-b"
        image="/static/images/balance-scales.svg"
        title="В сравнении пока ничего нет"
      >
        Добавляйте товары к сравнению и они появятся в данном разделе
      </UiEmptyState>
      <ModelHistory
        v-show="!selectedCategory || specifications[selectedCategory.id]"
        :no-padding="ids.length > 0"
        :is-fast="true"
        font-class="heading-l"
      />
    </div>
    <ClientOnly>
      <ModalsConfirmModal
        v-if="showConfirmModal"
        title="Удаление списка"
        :description="`Вы хотите удалить все товары категории «${modalCategoryName}» из сравнения? Действие невозможно отменить.`"
        apply-text="Удалить список"
        cancel-text="Отменить"
        :on-apply="() => scrollAfterRemove()"
        :on-close-action="() => $emit('close')"
        @close="showConfirmModal = false"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from '~/stores/global';
import { useCompareStore } from '~/stores/compare';
import { API } from '~/api/api';
import { getModel, modelsSpecification } from '~/api/pageModel';
import type {
  ModelCategory,
  ModelGroupSpecification,
  ResponseModel,
  ResponseModelGroupSpecs,
  ModelSpecification,
} from '~/interfaces/api';
import type {
  groupManySpecifications,
  ModelManySpecifications,
  sortedSpecifications,
  compareItem,
  DataCompare,
  SortedProducts,
  Storage,
  SpecStorage,
} from '~/interfaces/app';
import { base64ToObj, objToBase64 } from '~/utils/base64';

const ModelHistory = defineAsyncComponent(
  () => import('~/components/model-history/ModelHistory.vue')
);

const compareStore = useCompareStore();
const globalStore = useGlobalStore();
const selectedCategory = ref<ModelCategory>();
const categories = ref<ModelCategory[]>([]);
const productsByCategory = ref<SortedProducts>();
const specifications = ref<sortedSpecifications>({});
const specStorage = ref<SpecStorage>({});
const compareContainer = ref<HTMLElement>();
const showConfirmModal = ref(false);
const modalCategoryName = ref<string>('');
const ids = computed((): number[] => compareStore.ids);

async function getProducts() {
  try {
    const res = await API.get('/models', {
      params: { ids: ids.value.join(','), region_id: globalStore.region.id },
    });
    return res.data;
  } catch (err) {
    return null;
  }
}

async function getSpecification(sort: DataCompare = null): Promise<any> {
  const selected = sort ? sort.selectedCategory : selectedCategory.value;
  const productsBy = sort ? sort.productsByCategory : productsByCategory.value;
  if (
    selected?.id &&
    (productsBy as SortedProducts)?.[selected.id] &&
    (!specifications.value?.[selected.id] || sort)
  ) {
    const res = await Promise.all(
      (productsBy as SortedProducts)[selected.id].map((el: ResponseModel) =>
        modelsSpecification(el.id, globalStore.isBot)
      )
    );
    if (sort) return res;
    sortSpecification(res);
  }
}

function sortProducts(products: ResponseModel[], init = false) {
  const { c, pInC } = products.reduce(
    (sum, el) => {
      const newSum = { ...sum };
      if (!newSum.c.some((cat: ModelCategory) => cat.id === el.category.id))
        newSum.c.push(el.category);
      if (newSum?.pInC[el.category.id]) newSum?.pInC[el.category.id].push(el);
      else newSum!.pInC[el.category.id] = [el];

      return newSum;
    },
    { c: [] as ModelCategory[], pInC: {} as SortedProducts }
  );
  if (!init && c?.length) {
    selectedCategory.value = c[0];
    categories.value = c;
    productsByCategory.value = pInC;
  }

  return {
    selectedCategory: c[0],
    categories: c,
    productsByCategory: pInC,
  };
}

function sortSpecification(
  spec: ResponseModelGroupSpecs[],
  beforeNum = 0,
  rebuildId?: number | string
): void {
  const specCatId: string | number | undefined =
    rebuildId || selectedCategory.value?.id;
  if (specCatId && (!specifications.value?.[specCatId] || rebuildId)) {
    const storageSpec: Storage = specStorage.value?.[specCatId]
      ? { ...specStorage.value[specCatId] }
      : {};
    const newVal = spec.reduce(
      (
        sum: groupManySpecifications[],
        itemSpec: ResponseModelGroupSpecs,
        indxItem: number
      ) => {
        const newSum: groupManySpecifications[] = [...sum];
        const startItems = beforeNum + indxItem;
        itemSpec.forEach((specGroup: ModelGroupSpecification) => {
          const storedGroup = storageSpec[specGroup.group];
          if (storedGroup)
            specGroup.specifications.forEach((specRow: ModelSpecification) => {
              if (storedGroup.spec[specRow.title] !== undefined) {
                const val =
                  newSum[storedGroup.index].specifications[
                    storedGroup.spec[specRow.title]
                  ];
                val.value[startItems] = specRow.value;
                if (!val.different)
                  val.different = val.value[0] !== specRow.value;
              } else {
                storageSpec[specGroup.group].spec[specRow.title] =
                  newSum[storedGroup.index].specifications.length;
                newSum[storedGroup.index].specifications.push({
                  ...specRow,
                  value: [
                    ...(startItems ? Array(startItems).fill(null) : []),
                    specRow.value,
                  ],
                  different: !!startItems,
                });
              }
            });
          else {
            storageSpec[specGroup.group] = {
              index: newSum.length,
              spec: {},
            };
            const endItems = spec.length - indxItem - 1;
            newSum.push({
              ...specGroup,
              specifications: specGroup.specifications.map(
                (specRow: ModelSpecification, index4: number) => {
                  storageSpec[specGroup.group].spec[specRow.title] = index4;
                  return {
                    ...specRow,
                    value: [
                      ...(startItems ? Array(startItems).fill(null) : []),
                      specRow.value,
                      ...(endItems ? Array(endItems).fill(null) : []),
                    ],
                    different: !!startItems,
                  };
                }
              ),
            });
          }
        });
        return newSum;
      },
      specifications.value?.[specCatId] || []
    );
    const amount: number = productsByCategory.value?.[specCatId].length || 0;
    if (amount > 0) {
      specifications.value = {
        ...specifications.value,
        [specCatId]: newVal.map((el: groupManySpecifications) => ({
          ...el,
          specifications: el.specifications.map(
            (el2: ModelManySpecifications) =>
              el2.value.length === amount
                ? el2
                : {
                    ...el2,
                    value: [
                      ...el2.value,
                      ...Array(amount - el2.value.length).fill(null),
                    ],
                    different: true,
                  }
          ),
        })),
      };
      specStorage.value = {
        ...specStorage.value,
        [specCatId]: storageSpec,
      };
    }
  }
}

function onCategoryChange(category: ModelCategory): void {
  selectedCategory.value = category;
  getSpecification();
}

watch(
  () => ids.value,
  (newIds, oldIds) => {
    const needAdd = newIds.filter((i) => !oldIds.includes(i));
    const needDelete = oldIds.filter((i) => !newIds.includes(i));
    needAdd.forEach((id) => addItem(id));
    needDelete.forEach((id) => deleteItemById(id));
  }
);

function addItem(id: number) {
  getModel(String(id), { region_id: globalStore.region.id }).then(
    (res: ResponseModel) => {
      if (productsByCategory.value?.[res.category.id]) {
        productsByCategory.value[res.category.id].push(res);
        if (specifications.value?.[res.category.id])
          modelsSpecification(res.id, globalStore.isBot).then((spec) => {
            sortSpecification(
              [spec],
              specifications.value[res.category.id][0].specifications[0].value
                .length,
              res.category.id
            );
          });
      } else {
        categories.value.push(res.category);
        if (productsByCategory.value)
          productsByCategory.value[res.category.id] = [res];
        else {
          productsByCategory.value = { [res.category.id]: [res] };
          selectedCategory.value = res.category;
          getSpecification();
        }
      }
    }
  );
}

function deleteItemById(id: number): void {
  let index = 0;
  let catId = '';
  if (productsByCategory.value) {
    const haveId = Object.entries(productsByCategory.value).some((el) => {
      catId = el[0];
      index = el[1].findIndex((el2) => el2.id === id);
      return index > -1;
    });
    if (haveId) deleteItem(index, catId);
  }
}

function deleteItem(index: number, key?: string): void {
  const catId: string | number | undefined = key || selectedCategory.value?.id;
  if (catId && productsByCategory.value?.[catId]) {
    const prod: ResponseModel[] = [
      ...productsByCategory.value[catId].slice(0, index),
      ...productsByCategory.value[catId].slice(index + 1),
    ];

    const params: compareItem = {
      id: productsByCategory.value[catId][index].id,
      catId,
      deleteCard: true,
    };
    if (key === undefined) compareStore.toggleCompare(params);

    if (prod.length && specifications.value?.[catId]) {
      const storageSpec: Storage = { ...specStorage.value[catId] };
      specifications.value[catId] = specifications.value[catId].reduce(
        (specSum: groupManySpecifications[], el: groupManySpecifications) => {
          const newGroup = {
            ...el,
            specifications: el.specifications.reduce(
              (
                sum: ModelManySpecifications[],
                el2: ModelManySpecifications
              ) => {
                const value: (string | null)[] = [
                  ...el2.value.slice(0, index),
                  ...el2.value.slice(index + 1),
                ];
                const different = value.some(
                  (val: string | null) => val !== value[0]
                );
                if (different || value[0] !== null) {
                  storageSpec[el.group].spec[el2.title] = sum.length;

                  return [...sum, { ...el2, value, different }];
                }

                delete storageSpec[el.group].spec[el2.title];
                return sum;
              },
              []
            ),
          };

          if (newGroup.specifications.length > 0) {
            storageSpec[el.group].index = specSum.length;

            return [...specSum, newGroup];
          }

          delete storageSpec[el.group];
          return specSum;
        },
        []
      );
      specStorage.value[catId] = storageSpec;
      productsByCategory.value[catId] = prod;
    } else deleteCategory(false, key);
  }
}

function deleteCategory(toggle = true, key?: string) {
  const catId = key || selectedCategory.value?.id;
  if (productsByCategory.value && catId) {
    categories.value = categories.value.filter(
      (el: ModelCategory) => el.id !== catId
    );

    if (toggle)
      productsByCategory.value[catId]?.forEach((el) => {
        compareStore.toggleCompare({
          id: el.id,
          catId: Number(catId),
          deleteCard: true,
          hideNotification: true,
        });
      });

    if (categories.value.length) {
      delete productsByCategory.value[catId];
      delete specifications.value[catId];
      delete specStorage.value[catId];
      if (!key || catId === selectedCategory.value?.id)
        selectedCategory.value = categories.value[0];
      getSpecification();
    } else {
      selectedCategory.value = productsByCategory.value = undefined;
      specifications.value = specStorage.value = {};
    }
  }
}

function confirmDelete() {
  modalCategoryName.value = selectedCategory.value?.name
    ? selectedCategory.value?.name[0].toUpperCase() +
      selectedCategory.value?.name.slice(1)
    : 'Не задано';
  showConfirmModal.value = true;
}

function scrollAfterRemove() {
  deleteCategory();
  if (window && compareContainer.value) {
    const y = compareContainer.value.getBoundingClientRect().top - 90;
    window.scrollTo({
      top: window.scrollY + y,
      left: 0,
      behavior: 'smooth',
    });
  }
}

const dataCompare = await useAsyncData('dataCompare', async () => {
  if (ids.value.length) {
    const products = await getProducts();
    const sort = sortProducts(products);
    // @ts-ignore
    const specification = await getSpecification(sort);
    return objToBase64({
      data: {
        sort,
        specification,
      },
    });
  }
  return null;
});
if (dataCompare.data.value && ids.value.length) {
  const data = base64ToObj(dataCompare.data.value).data;
  if (data?.sort && data?.sort?.selectedCategory) {
    const sort = data?.sort;
    selectedCategory.value = sort.selectedCategory;
    categories.value = sort.categories;
    productsByCategory.value = sort.productsByCategory;
  } else {
    compareStore.setCompareState({ ids: [], count: {} });
  }
  if (data?.specification) {
    sortSpecification(data?.specification);
  }
}
</script>
<style lang="scss" scoped>
.compare-page {
  .compare-wrap {
    .clear-category {
      display: inline-flex;
      margin-top: 26px;
      @include lg-block {
        margin-top: 32px;
      }

      :deep(.icon) {
        min-width: 20px;
        height: 20px;
        @include lg-block {
          height: 24px;
          min-width: 24px;
        }
      }
    }

    .categories-list {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      padding: 0 0 16px 0;
      margin: 0;
      flex-flow: row nowrap;
      @include md-block {
        padding: 0 0 24px 0;
      }
      @include lg-block {
        padding: 0 0 32px 0;
        margin: 0 0 -8px -8px;
        flex-flow: row wrap;
      }
    }

    .category-btn {
      margin: 0 4px 0 0;

      @include lg-block {
        margin: 0 0 8px 8px;
      }

      .category-count {
        color: $achromatic-disabled;
        margin-left: 6px;
        @include lg-block {
          margin-left: 8px;
        }
      }

      .category-name {
        span {
          display: inline-block;

          &:first-letter {
            text-transform: uppercase;
          }
        }
      }
    }

    .products-compare {
      margin: 0 -12px;
      @include md-block {
        margin: 0 -20px;
      }
      @include lg-block {
        margin: 0;
      }
    }
  }

  .model-history {
    padding-top: 40px;
    @include md-block {
      padding-top: 48px;
    }
    @include lg-block {
      padding-top: 64px;
    }
  }
}

:deep(.empty-state) {
  margin-top: 0;
}
</style>
